import React, { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { captureException } from '@sentry/react';
import { useTranslate } from '@bynder/localization';
import { Button, Flex, notify } from '@bynder/design-system';
import { IconAIImage, IconCancel, IconOpenInNew } from '@bynder/icons';
import { AssetPickerOptions, GenAIFromPromptModal } from '@bynder/image-gen-frontend-lib';
import { getJWT } from '@bynder/js-access-token-lib';
import { useSplit } from '../../helpers/useSplit';
import { useImageGenUserPermissions } from './useImageGenPermissions';
import { ReferenceAssetPicker } from './ReferenceAssetPicker';
import { ReferenceAsset } from './utils';
import { buildSaveBody, SaveMetapropertyValue } from './buildSaveBody';
import { getSaveErrorNotificationKeys } from './getSaveErrorNotificationKeys';
import { FloatingContainer, StyledNotificationDescription } from './styled';

export function GenerateWithPrompt() {
	const { translate } = useTranslate();
	const {
		allowedTools,
		canGenerateWithPrompt,
		isApprovalRequired,
		canCreateAssets,
		canDownloadOriginalAssets,
	} = useImageGenUserPermissions();
	// Lets users write metaproperty values in the save dialog; needs the save endpoint to accept them.
	const isMetapropertyValuesOn = useSplit('ai_agents_metaproperty_values');
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const [sessionId, setSessionId] = React.useState(uuid());

	const [addReferenceAssetsCallback, setAddReferenceAssetsCallback] =
		useState<(assets: ReferenceAsset[]) => void>();
	const [assetPickerOptions, setAssetPickerOptions] = useState<AssetPickerOptions>();
	const isReferenceAssetsPickerOpen = Boolean(addReferenceAssetsCallback);

	const openReferenceAssetsPicker = useCallback(
		(callback: (assets: ReferenceAsset[]) => void, options?: AssetPickerOptions) => {
			setAddReferenceAssetsCallback(() => callback);
			setAssetPickerOptions(options);
		},
		[],
	);

	const closeAssetPicker = useCallback(() => {
		setAddReferenceAssetsCallback(undefined);
		setAssetPickerOptions(undefined);
	}, []);

	const openModal = React.useCallback(() => {
		setSessionId(uuid());
		setIsModalOpen(true);
	}, []);

	const onClose = React.useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const isSaveToWaitingRoom = isApprovalRequired && !canCreateAssets;

	const onSave = useCallback(
		async ({
			enhancementId,
			name,
			metapropertyValues,
		}: {
			enhancementId: number;
			name?: string;
			metapropertyValues?: SaveMetapropertyValue[];
		}) => {
			const jwt = await getJWT();

			const body = buildSaveBody({
				name,
				// The dialog only offers values with the split on; never send them otherwise.
				metapropertyValues: isMetapropertyValuesOn ? metapropertyValues : undefined,
			});

			const response: void | Response = await fetch(
				`/v7/image-gen/enhancements/${enhancementId}/save`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${jwt}`,
						...(body && { 'Content-Type': 'application/json' }),
					},
					...(body && { body }),
				},
			).catch((error) => {
				captureException(error);
			});

			if (!response || !response.ok) {
				const errorCode = await readSaveErrorCode(response);
				const { title, description } = getSaveErrorNotificationKeys(errorCode);

				notify({
					variant: 'error',
					title: translate(title),
					description: translate(description),
				});

				return;
			}

			const json: { mediaId: string; assetName?: string } = await response.json();

			const notificationId = notify({
				variant: 'success',
				title: isSaveToWaitingRoom
					? translate('generateWithPrompt.save-notification.title.waiting-room')
					: translate('generateWithPrompt.save-notification.title.dam'),
				description: isSaveToWaitingRoom ? (
					translate('generateWithPrompt.save-notification.description.waiting-room')
				) : (
					<StyledNotificationDescription>
						{/* Remove unnecessary nullish coalescing operator when assetName is provided
						by BE */}
						{json.assetName ?? 'Generated Asset'}
					</StyledNotificationDescription>
				),
				button: (
					<Flex>
						<Button
							variant="clean"
							onClick={() => {
								window.open(
									isSaveToWaitingRoom
										? `https://${location.hostname}/waitingroom/`
										: `https://${location.hostname}/media/?mediaId=${json.mediaId}`,
									'_blank',
								);

								notify.dismiss(notificationId);
							}}
							icon={<IconOpenInNew />}
							title={
								isSaveToWaitingRoom
									? translate(
											'generateWithPrompt.save-notification.link.waiting-room',
									  )
									: translate('generateWithPrompt.save-notification.link.dam')
							}
						/>
						<Button
							variant="clean"
							onClick={() => {
								notify.dismiss(notificationId);
							}}
							icon={<IconCancel />}
							title={translate('generateWithPrompt.save-notification.link.close')}
						/>
					</Flex>
				),
			});
		},
		[isSaveToWaitingRoom, isMetapropertyValuesOn, translate],
	);

	if (!canGenerateWithPrompt) {
		return null;
	}

	return (
		<FloatingContainer>
			<Button
				variant="secondary"
				icon={<IconAIImage />}
				onClick={openModal}
				title={translate('generateWithPrompt.title')}
			/>
			<GenAIFromPromptModal
				isOpen={isModalOpen}
				onClose={onClose}
				getJWT={getJWT}
				sessionId={sessionId}
				clientName="API"
				onSave={onSave}
				showAssetPicker={openReferenceAssetsPicker}
				closeAssetPicker={closeAssetPicker}
				allowedTools={allowedTools}
				isDownloadAvailable={canDownloadOriginalAssets}
				renameModalActions={['save', 'download']}
				allowMetapropertyValues={isMetapropertyValuesOn}
			/>
			<ReferenceAssetPicker
				isOpen={isReferenceAssetsPickerOpen}
				onClose={closeAssetPicker}
				onSelect={(assets) => {
					addReferenceAssetsCallback?.(assets);
					closeAssetPicker();
				}}
				allowDocuments={assetPickerOptions?.allowDocuments}
				maxAssets={assetPickerOptions?.maxAssets}
			/>
		</FloatingContainer>
	);
}

async function readSaveErrorCode(response: Response | void): Promise<string | undefined> {
	if (!response) {
		return undefined;
	}

	try {
		const body = (await response.json()) as { errorCode?: unknown };

		return typeof body?.errorCode === 'string' ? body.errorCode : undefined;
	} catch {
		// Non-JSON or empty error body (e.g. a gateway HTML page) — fall back to
		// the generic save-error message.
		return undefined;
	}
}
